import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { toast } from 'react-toastify';
import { saveQuotation } from '../../models/quotationModel';
import { getSupplierNames } from '../../models/specificationModel';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import MultiFileInput from '../common/MultiFileInput';
import { stringify } from 'uuid';

// Helper function to parse PHP serialized data
function parsePHPSerializedArray(serializedString) {
  if (!serializedString) return [];
  
  try {
    // Handle PHP serialized arrays like: a:2:{i:0;s:3:"610";i:1;s:4:"1500";}
    const results = [];
    
    // First try to match string values: s:length:"value"
    const stringMatches = serializedString.match(/s:\d+:"([^"]*)"/g);
    if (stringMatches && stringMatches.length > 0) {
      return stringMatches.map(match => match.match(/"([^"]*)"/)[1]);
    }
    
    // If no strings found, try to match values in array format
    // This handles cases like: a:2:{i:0;d:91.5;i:1;i:0;}
    const arrayMatch = serializedString.match(/a:\d+:\{(.+)\}/);
    if (arrayMatch) {
      const content = arrayMatch[1];
      // Split by index patterns and extract values
      const parts = content.split(/i:\d+;/);
      const values = [];
      
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i];
        if (part.startsWith('i:')) {
          // Integer value: i:0; or i:-5;
          const intMatch = part.match(/i:(-?\d+)/);
          if (intMatch) {
            values.push(parseInt(intMatch[1]));
          }
        } else if (part.startsWith('d:')) {
          // Double/decimal value: d:91.5; or d:-91.5;
          const doubleMatch = part.match(/d:(-?[0-9.]+)/);
          if (doubleMatch) {
            values.push(parseFloat(doubleMatch[1]));
          }
        } else if (part.startsWith('s:')) {
          // String value: s:length:"value"
          const stringMatch = part.match(/s:\d+:"([^"]*)"/);
          if (stringMatch) {
            values.push(stringMatch[1]);
          }
        }
      }
      return values;
    }
    
    return results;
  } catch (error) {
    console.error('Error parsing PHP serialized data:', error);
    return [];
  }
}

function AttachQuotationModal({ isOpen, onClose, specificationId, onQuotationSaved, onSuccess, specification, suppliers = [], selectedSupplier = null, existingQuote = null }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [vats, setVats] = useState({});
  const [vatTotal, setVatTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [comments, setComments] = useState({});
  const [selectedSupplierCode, setSelectedSupplierCode] = useState('');
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const SA_VAT_RATE = 15; // South African VAT rate
  
  const [formData, setFormData] = useState({
    company_name: '',
    tel: '',
    physical_address: '',
    email_address: '',
    quotation_files: []
  });
  
  // Add state for attachments (same as EditSpecification)
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Pre-select supplier if provided
  useEffect(() => {
    if (isOpen && selectedSupplier) {
      // Log selected supplier details for debugging when modal opens
      console.log('=== ATTACH QUOTE MODAL OPENED ===');
      console.log('Selected supplier prop:', selectedSupplier);
      console.log('===============================');
      
      // Set the selected supplier code (always as string)
      setSelectedSupplierCode(String(selectedSupplier.company_code));
      
      // Pre-fill supplier details
      setFormData(prevFormData => {
        const newFormData = {
          ...prevFormData,
          company_code: String(selectedSupplier.company_code),
          company_name: selectedSupplier.company_name || `Supplier ${selectedSupplier.company_code}`
        };
        
        console.log('=== FORM DATA AFTER SUPPLIER PRE-FILL ===');
        console.log('New form data:', newFormData);
        console.log('========================================');
        
        return newFormData;
      });
      
      // Fetch additional supplier details if needed
      const fetchSupplierDetails = async () => {
        try {
          const response = await api.get(`/api/suppliers/${encodeURIComponent(selectedSupplier.company_code)}`);
          
          if (response.data.status === 'success' && response.data.data) {
            const supplier = response.data.data;
            
            setFormData(prevFormData => {
              // Split comma-separated emails and use only the first one
              const emailList = supplier.E_Mail || '';
              const firstEmail = emailList.split(',')[0].trim();
              
              const updatedFormData = {
                ...prevFormData,
                // Preserve the company code and update the company name with API result
                company_code: prevFormData.company_code || selectedSupplier.company_code,
                company_name: supplier.BP_Name || prevFormData.company_name,
                email_address: firstEmail,
                tel: supplier.Tel || '',
                physical_address: supplier.Address || ''
              };
              
              console.log('=== AFTER API SUPPLIER FETCH ===');
              console.log('API supplier data:', supplier);
              console.log('Previous form data:', prevFormData);
              console.log('Updated form data:', updatedFormData);
              console.log('===============================');
              
              return updatedFormData;
            });
          }
        } catch (error) {
          console.error('Error fetching pre-selected supplier details:', error);
        }
      };
      
      fetchSupplierDetails();
    } else if (isOpen && !selectedSupplier) {
      // Clear supplier selection when modal opens without a pre-selected supplier
      setSelectedSupplierCode('');
      setFormData(prevFormData => ({
        ...prevFormData,
        company_code: '',
        company_name: '',
        email_address: '',
        tel: '',
        physical_address: ''
      }));
    }
  }, [isOpen, selectedSupplier]);

  // Fetch attachments when modal opens - for quotations we need the quotation_id
  // Use existingQuote (for edit mode) or specification.details (for view mode)
  useEffect(() => {
    const quotationId = existingQuote?.quotation_id || specification?.details?.quotation_id;
    if (isOpen && quotationId) {
      fetchQuotationAttachments(quotationId);
    }
  }, [isOpen, existingQuote?.quotation_id, specification?.details?.quotation_id]);

  // Initialize data when specification changes or when modal is opened
  useEffect(() => {
    // Safety check to ensure specification exists and is properly structured
    if (isOpen && specification) {
      // Don't reset supplier form data if we have a pre-selected supplier
      // The supplier-specific useEffect will handle that
      if (!selectedSupplier) {
        // Reset form data only if no pre-selected supplier
        setFormData({
          company_name: '',
          tel: '',
          physical_address: '',
          email_address: '',
          quotation_files: []
        });
        
        // Do NOT pre-select supplier code if none is pre-selected
        setSelectedSupplierCode('');
      }
 
      const specItems = Array.isArray(specification.specification_items) 
        ? specification.specification_items 
        : (specification.specification_items ? [specification.specification_items] : []);
      
      const specQty = Array.isArray(specification.specification_qty)
        ? specification.specification_qty
        : (specification.specification_qty ? [specification.specification_qty] : []);
        
      // Check if we have existing quotation data - either from existingQuote prop or specification.details
      // existingQuote prop takes priority (used when editing a specific quote in ESR)
      const quoteToEdit = existingQuote || specification.details;
      const hasExistingQuotation = quoteToEdit && 
                                   quoteToEdit.quotation_id && 
                                   (quoteToEdit.quotation_amounts || quoteToEdit.quotation_units);
      
      let existingAmounts = [];
      let existingUnits = [];
      let existingComments = [];
      let existingVats = [];
      
      if (hasExistingQuotation) {
        // Handle both array (API already unserialized) and serialized string formats
        try {
          // quotation_amounts
          if (Array.isArray(quoteToEdit.quotation_amounts)) {
            existingAmounts = quoteToEdit.quotation_amounts;
          } else if (typeof quoteToEdit.quotation_amounts === 'string') {
            existingAmounts = parsePHPSerializedArray(quoteToEdit.quotation_amounts);
          }
          
          // quotation_units
          if (Array.isArray(quoteToEdit.quotation_units)) {
            existingUnits = quoteToEdit.quotation_units;
          } else if (typeof quoteToEdit.quotation_units === 'string') {
            existingUnits = parsePHPSerializedArray(quoteToEdit.quotation_units);
          }
          
          // quotation_comments
          if (Array.isArray(quoteToEdit.quotation_comments)) {
            existingComments = quoteToEdit.quotation_comments;
          } else if (typeof quoteToEdit.quotation_comments === 'string') {
            existingComments = parsePHPSerializedArray(quoteToEdit.quotation_comments);
          }
          
          // quotation_vats
          if (Array.isArray(quoteToEdit.quotation_vats)) {
            existingVats = quoteToEdit.quotation_vats;
          } else if (typeof quoteToEdit.quotation_vats === 'string') {
            existingVats = parsePHPSerializedArray(quoteToEdit.quotation_vats);
          }
          
          console.log('=== LOADING EXISTING QUOTE DATA ===');
          console.log('Quote to edit:', quoteToEdit);
          console.log('Existing amounts:', existingAmounts);
          console.log('Existing units:', existingUnits);
          console.log('===================================');

        } catch (error) {
          console.error('Error parsing existing quotation data:', error);
        }
      }
        
      // Create items array with quantities and existing data
      const newItems = specItems.map((item, index) => {
        const unitPrice = existingUnits[index] || '';
        const total = existingAmounts[index] ? parseFloat(existingAmounts[index]) : 0;
        const vatAmount = existingVats[index] ? parseFloat(existingVats[index]) : 0;
        
        // Determine if VAT should be included based on existing VAT amount
        // If there's a VAT amount > 0, then VAT should be included
        const shouldIncludeVat = vatAmount > 0;
        

        
        return {
          id: index,
          name: item,
          quantity: specQty[index] || 0,
          unitPrice: unitPrice,
          total: total,
          includeVat: shouldIncludeVat
        };
      });
      

      
      setItems(newItems);
      
      // Initialize vats object with existing data
      const newVats = {};
      newItems.forEach(item => {
        newVats[item.id] = existingVats[item.id] ? parseFloat(existingVats[item.id]) : 0;
      });

      setVats(newVats);
      
      // Initialize comments with existing data
      const newComments = {};
      const newVisibleComments = {};
      newItems.forEach(item => {
        newComments[item.id] = existingComments[item.id] || '';
        newVisibleComments[item.id] = false;
      });
      setComments(newComments);
      setVisibleComments(newVisibleComments);
      
      // Set totals from existing data
      if (hasExistingQuotation) {
        // Use existing totals if available - use quoteToEdit instead of specification.details
        const existingVatTotal = quoteToEdit.quotation_vat || 0;
        const existingGrandTotal = quoteToEdit.quotation_total || 0;
        
        setVatTotal(existingVatTotal);
        setGrandTotal(existingGrandTotal);
        

        
        // If there's a mismatch between stored VAT total and calculated VAT from items,
        // recalculate to ensure consistency
        const calculatedVatTotal = Object.values(newVats).reduce((sum, vat) => sum + (parseFloat(vat) || 0), 0);
        if (Math.abs(calculatedVatTotal - existingVatTotal) > 0.01) {
          console.log('VAT mismatch detected, recalculating...');
          calculateTotals(newItems, newVats);
        }
      } else {
        // Calculate totals for new quotation
        calculateTotals(newItems, newVats);
      }
      
      // When editing an existing quote OR when a supplier is pre-selected,
      // don't reset supplier data - it's handled by the supplier-specific useEffect
      // Only reset form data when creating a new quotation without pre-selection
      if (!existingQuote && !selectedSupplier) {
        // Do NOT pre-fill supplier details - user must select manually
        // Keep form blank so user explicitly chooses supplier
        setFormData({
          company_name: '',
          tel: '',
          physical_address: '',
          email_address: '',
          quotation_files: []
        });
      
        // Do NOT pre-select supplier code - user must choose manually
        setSelectedSupplierCode('');
      }
    }
  }, [isOpen, specification, existingQuote, selectedSupplier]);

  // Fetch supplier names for dropdown when modal opens
  useEffect(() => {
    const fetchSupplierNames = async () => {

      
      if (isOpen && specification?.specification_supplier && specification.specification_supplier.length > 0) {
        try {
          const supplierCodes = specification.specification_supplier.map(c => String(c));
          // Filter out suppliers that already have submitted quotes
          const submittedSupplierCodes = specification.submissions ? 
            specification.submissions.map(quote => String(quote.quotation_company_code || '')).filter(Boolean) : [];
          
          let availableSupplierCodes = supplierCodes.filter(code => 
            !submittedSupplierCodes.includes(String(code))
          );
          
          // If we have a pre-selected supplier and it's valid (not already submitted a quote),
          // only show that supplier in the available suppliers list
          if (selectedSupplier && selectedSupplier.company_code) {
            // If pre-selected supplier is valid (hasn't submitted a quote)
            if (availableSupplierCodes.includes(String(selectedSupplier.company_code))) {
              // Only use the pre-selected supplier
              availableSupplierCodes = [String(selectedSupplier.company_code)];
            }
          }
          
          if (availableSupplierCodes.length === 0) {
            setAvailableSuppliers([]);
            return;
          }
          
          // Get the supplier names for the dropdown
          const supplierNames = await getSupplierNames(availableSupplierCodes);
          console.log('Received supplier names:', supplierNames);
          
          // Create supplier options array
          const supplierOptions = availableSupplierCodes.map(code => ({
            code: code,
            name: supplierNames[code] || `Supplier ${code}`
          }));
          
          setAvailableSuppliers(supplierOptions);
          
          // Auto-populate if only one supplier available AND no pre-selected supplier
          if (supplierOptions.length === 1 && !selectedSupplier) {
            const singleSupplier = supplierOptions[0];
            console.log('🎯 Only one supplier available, auto-selecting:', singleSupplier);
            setSelectedSupplierCode(singleSupplier.code);
            
            // Fetch and populate supplier details
            try {
              const response = await api.get(`/api/suppliers/${encodeURIComponent(singleSupplier.code)}`);
              console.log('Auto-fetched supplier details:', response.data);
              
              if (response.data.status === 'success' && response.data.data) {
                const supplier = response.data.data;
                
                // Split comma-separated emails and use only the first one
                const emailList = supplier.E_Mail || '';
                const firstEmail = emailList.split(',')[0].trim();
                
                setFormData({
                  company_code: singleSupplier.code,
                  company_name: supplier.BP_Name || singleSupplier.name,
                  email_address: firstEmail,
                  tel: supplier.Tel || '',
                  physical_address: supplier.Address || '',
                  quotation_files: []
                });
              } else {
                // Fallback to just the name
                setFormData({
                  company_code: singleSupplier.code,
                  company_name: singleSupplier.name,
                  email_address: '',
                  tel: '',
                  physical_address: '',
                  quotation_files: []
                });
              }
            } catch (error) {
              console.error('Error auto-fetching supplier details:', error);
              // Fallback to just the name
              setFormData({
                company_code: singleSupplier.code,
                company_name: singleSupplier.name,
                email_address: '',
                tel: '',
                physical_address: '',
                quotation_files: []
              });
            }
          }
        } catch (error) {
          console.error('Error fetching supplier names:', error);
          // Fallback: create supplier options with codes only
          const supplierCodes = specification.specification_supplier.map(c => String(c));
          
          // Still need to filter out submitted quotes even in fallback
          const submittedSupplierCodes = specification.submissions ? 
            specification.submissions.map(quote => String(quote.quotation_company_code || '')).filter(Boolean) : [];
            
          const availableSupplierCodes = supplierCodes.filter(code => 
            !submittedSupplierCodes.includes(String(code))
          );
          
          const fallbackOptions = availableSupplierCodes.map(code => ({
            code: code,
            name: `Supplier ${code}`
          }));
          setAvailableSuppliers(fallbackOptions);
          
          // Auto-select single supplier even in fallback, but only if no pre-selected supplier
          if (fallbackOptions.length === 1 && !selectedSupplier) {
            const singleSupplier = fallbackOptions[0];
            setSelectedSupplierCode(singleSupplier.code);
            setFormData({
              company_code: singleSupplier.code,
              company_name: singleSupplier.name,
              email_address: '',
              tel: '',
              physical_address: '',
              quotation_files: []
            });
          }
        }
      }
    };

    fetchSupplierNames();
  }, [isOpen, specification?.specification_supplier, specification?.submissions]);

  // Handle supplier selection change
  const handleSupplierChange = async (e) => {
    const supplierCode = e.target.value;
    setSelectedSupplierCode(supplierCode);
    
    if (supplierCode) {
      try {
        // Fetch details for just this one supplier  
        const response = await api.get(`/api/suppliers/${encodeURIComponent(supplierCode)}`);
        
        if (response.data.status === 'success' && response.data.data) {
          const supplier = response.data.data; // API now returns single object, not array
          
          // Split comma-separated emails and use only the first one
          const emailList = supplier.E_Mail || '';
          const firstEmail = emailList.split(',')[0].trim();
          
          setFormData({
            ...formData,
            company_code: supplierCode,
            company_name: supplier.BP_Name || '',
            email_address: firstEmail,
            tel: supplier.Tel || '',
            physical_address: supplier.Address || ''
          });
        } else {
          // Fallback to just the name from availableSuppliers
          const fallbackSupplier = availableSuppliers.find(s => s.code === supplierCode);
          if (fallbackSupplier) {
            setFormData({
              ...formData,
              company_code: supplierCode,
              company_name: fallbackSupplier.name || '',
              email_address: '',
              tel: '',
              physical_address: ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching supplier details:', error);
        // Fallback to just the name from availableSuppliers
        const fallbackSupplier = availableSuppliers.find(s => s.code === supplierCode);
        if (fallbackSupplier) {
          setFormData({
            ...formData,
            company_code: supplierCode,
            company_name: fallbackSupplier.name || '',
            email_address: '',
            tel: '',
            physical_address: ''
          });
        }
      }
    } else {
      // Clear the form if no supplier selected
      setFormData({
        ...formData,
        company_code: '',
        company_name: '',
        email_address: '',
        tel: '',
        physical_address: ''
      });
    }
  };

  const handleInputChange = (e) => {
    // No need to prevent default for normal input changes
    // as this would interfere with normal form behavior
    
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleFileChange = (files) => {
    // Prevent form submission when files are selected
    setFormData({
      ...formData,
      quotation_files: files
    });
  };

  // Fetch existing attachments for quotation
  const fetchQuotationAttachments = async (quotationIdParam) => {
    const quotationId = quotationIdParam || existingQuote?.quotation_id || specification?.details?.quotation_id;
    if (!quotationId) return;
    
    try {
      const response = await api.get(`/api/attachments/quotations/${quotationId}/list?type=quotation`);
      console.log('Quotation attachments response:', response.data);
      
      // Handle nested response structure: response.data.data.attachments
      const attachments = response.data?.data?.attachments || response.data?.attachments || [];
      setAttachments(attachments);
      console.log('Set quotation attachments:', attachments);
    } catch (error) {
      console.error('Error fetching quotation attachments:', error);
    }
  };

  // Handle file selection (collect files, don't upload yet)
  const handleFileUpload = async (fileArray) => {
    if (!fileArray || fileArray.length === 0) {
      return;
    }
    
    // Just collect the files, they'll be uploaded after quotation is saved
    setAttachments(prev => [...prev, ...fileArray]);
    //toast.success(`${fileArray.length} file(s) selected. They will be uploaded when you save the quotation.`);
    return;
  };

  // Upload files to server after quotation is saved
  const uploadFilesToServer = async (quotationId) => {
    if (!attachments || attachments.length === 0) {
      return true; // No files to upload
    }

    setUploading(true);
    try {
      // Filter to only upload File objects (not already uploaded attachments)
      const filesToUpload = attachments.filter(item => item instanceof File);
      
      if (filesToUpload.length === 0) {
        return true;
      }

      const formData = new FormData();
      filesToUpload.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
      formData.append('type', 'quotation');

      const response = await api.post(`/api/attachments/quotations/${quotationId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        return true;
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Failed to upload files: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Handle file download (using legacy pattern)
  const handleFileDownload = (filename) => {
    const { API_BASE_URL } = require('../../config');
    // Use backend controller for forced download (legacy pattern)
    const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(filename)}`;
    window.open(downloadUrl, '_blank');
  };

  // Handle file deletion
  const handleFileDelete = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await api.delete(`/api/attachments/quotations/${filename}`);
      console.log('Delete response:', response.data);
      
      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        // Refresh attachments list
        await fetchQuotationAttachments();
        toast.success('File deleted successfully!');
      } else {
        throw new Error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      // Only show error if it's not a successful HTTP status code in disguise
      if (error.response?.status >= 400 || !error.response?.status) {
        toast.error('Failed to delete file: ' + (error.response?.data?.message || error.message));
      } else {
        // If it was actually successful, refresh attachments
        await fetchQuotationAttachments();
        toast.success('File deleted successfully!');
      }
    }
  };
  
  const handleItemPriceChange = (id, value) => {
    // Update the unit price for the item
    const updatedItems = items.map(item => {
      if (item.id === id) {
        // Remove currency formatting and spaces for calculation
        // This regex removes: R, spaces, commas, and any other non-digit/decimal/negative characters
        const cleanValue = value.replace(/[^\d.-]/g, '');
        const unitPrice = parseFloat(cleanValue) || 0;
        const total = unitPrice * item.quantity;
        
        console.log(`Price change for item ${id}: input="${value}", cleaned="${cleanValue}", unitPrice=${unitPrice}, quantity=${item.quantity}, total=${total}`);
        
        return {
          ...item,
          unitPrice: cleanValue, // Store clean value without formatting
          total
        };
      }
      return item;
    });
    
    // Create updated VAT values based on new totals
    const updatedVats = { ...vats };
    updatedItems.forEach(item => {
      if (item.id === id && item.includeVat) {
        updatedVats[id] = item.total * SA_VAT_RATE / 100;
        console.log(`Updated VAT for item ${id}: ${updatedVats[id]} (price changed, includeVat: ${item.includeVat})`);
      } else if (item.id === id && !item.includeVat) {
        updatedVats[id] = 0;
      }
    });
    
    setItems(updatedItems);
    setVats(updatedVats);
    calculateTotals(updatedItems, updatedVats);
  };
  
  const toggleVat = (id) => {
    // Toggle VAT inclusion for this item
    const updatedItems = items.map(item => {
      if (item.id === id) {
        return { ...item, includeVat: !item.includeVat };
      }
      return item;
    });
    
    // Update VAT amounts
    const updatedVats = { ...vats };
    updatedItems.forEach(item => {
      if (item.id === id) {
        // Calculate VAT amount if VAT is included, otherwise set to 0
        updatedVats[id] = item.includeVat ? (item.total * SA_VAT_RATE / 100) : 0;
      }
    });
    
    setItems(updatedItems);
    setVats(updatedVats);
    calculateTotals(updatedItems, updatedVats);
  };
  
  const calculateTotals = (currentItems, currentVats) => {
    // Calculate total VAT
    const totalVat = Object.values(currentVats).reduce((sum, vat) => sum + (parseFloat(vat) || 0), 0);
    
    // Calculate grand total (items + VAT)
    const itemsTotal = currentItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const total = itemsTotal + totalVat;
    
    setVatTotal(totalVat);
    setGrandTotal(total);
  };
  
  const handleCommentChange = (id, value) => {
    setComments(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  // Track which comments are being edited
  const [visibleComments, setVisibleComments] = useState({});
  
  const toggleComment = (id) => {
    // Toggle the visibility of the comment input field
    setVisibleComments(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if at least one item has a price (allow negative values)
    const hasPrice = items.some(item => parseFloat(item.unitPrice) !== 0 && !isNaN(parseFloat(item.unitPrice)));
    if (!hasPrice) {
      toast.error('Please enter at least one item price');
      return;
    }

    // For ESR specifications (specification_emergency == 1), file attachment is required
    const isESR = specification?.specification_emergency === 1 || specification?.specification_emergency === '1';
    // Count both File objects (new) and existing attachments
    const hasFiles = attachments.some(item => item instanceof File) || 
                     attachments.some(item => item.attachment_id);
    if (isESR && !hasFiles) {
      toast.error('File attachment is required for ESR quotations.');
      return;
    }

    // Validate supplier code is present (check both selectedSupplierCode and formData.company_code)
    const supplierCode = selectedSupplierCode || formData.company_code;
    if (!supplierCode || supplierCode.toString().trim() === '') {
      toast.error('Supplier must be selected before submitting quotation');
      return;
    }

    // Validate company name is present
    if (!formData.company_name || formData.company_name.trim() === '') {
      toast.error('Company name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      // Use supplier name from form data or specification details
      const supplierName = formData.company_name || 'Unknown Supplier';
      
      // Format data for API
      const quotationData = new FormData();
      // Use rfq field for quotation_procurement_id as expected by the backend
      quotationData.append('rfq', specification.procurement_id); 
      quotationData.append('requisition_id', specification?.requisition_id);
      quotationData.append('spec', specificationId);
      
      // Use existing quotation ID if available (from existingQuote prop or specification.details), otherwise create new one
      const quoteToEdit = existingQuote || specification?.details;
      const quotationId = quoteToEdit?.quotation_id || null;
      
      console.log('=== QID DEBUG ===');
      console.log('existingQuote?.quotation_id:', existingQuote?.quotation_id);
      console.log('specification?.details?.quotation_id:', specification?.details?.quotation_id);
      console.log('quotationId after || null:', quotationId);
      console.log('typeof quotationId:', typeof quotationId);
      console.log('quotationId truthy check:', !!quotationId);
      
      // Only append qid if quotation_id exists and is not null (for updates)
      // Don't append qid if quotation_id is null/undefined (for new quotations)
      if (quotationId != null) {
        console.log('Appending qid for UPDATE:', quotationId);
        quotationData.append('qid', String(quotationId)); // Convert to string to preserve precision
      } else {
        console.log('NOT appending qid - NEW quotation');
      }
      console.log('================');

      console.log("Qid Value:", quotationId);
      console.log("Qid Type:", typeof quotationId); 
      
      quotationData.append('directory', '');
      quotationData.append('code', String(supplierCode));
      
      quotationData.append('company_name', supplierName);
      quotationData.append('tel', formData.tel);
      quotationData.append('physical_address', formData.physical_address);
      quotationData.append('email_address', formData.email_address);
      
      // Add selected supplier code
      quotationData.append('company_code', String(supplierCode));
      
      quotationData.append('quotation_total', grandTotal.toString());
      quotationData.append('quotation_vat', vatTotal.toString());
      
      // Add item data
      const itemUnitPrices = items.map(item => item.unitPrice);
      const itemTotals = items.map(item => item.total);
      
      // Format arrays consistently for PHP - needs JSON strings
      quotationData.append('quotation_units', JSON.stringify(itemUnitPrices));
      quotationData.append('quotation_amounts', JSON.stringify(itemTotals));
      
      // Ensure comments and VATs are properly formatted as JSON strings
      const commentsArray = [];
      const vatsArray = [];
      items.forEach(item => {
        commentsArray.push(comments[item.id] || '');
        vatsArray.push(vats[item.id] || 0);
      });
      
      quotationData.append('quotation_comments', JSON.stringify(commentsArray));
      quotationData.append('quotation_vats', JSON.stringify(vatsArray));
  
      // Note: Files are now handled separately through the standardized attachment system
      // No need to append files to quotation data as they're uploaded via /api/attachments/specifications/{id}
      quotationData.append('status', 3); 
      
      const result = await saveQuotation(quotationData);
      
      if (result.status === 1) {
        // Get the quotation ID from response (for new quotations) or use existing one
        const savedQuotationId = result.quotation_id || quotationId;
        
        // Upload files if any were selected
        if (attachments && attachments.length > 0) {
          try {
            await uploadFilesToServer(savedQuotationId);
            toast.success('Quotation and files saved successfully!');
          } catch (uploadError) {
            // Quotation saved but files failed - show warning
            toast.warning('Quotation saved but some files failed to upload: ' + uploadError.message);
          }
        } else {
          toast.success('Quotation attached successfully');
        }
        
        // Call both callbacks if they exist
        if (typeof onQuotationSaved === 'function') {
          onQuotationSaved();
        }
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        onClose();
      } else {
        toast.error(result.message || 'Failed to attach quotation');
      }
    } catch (error) {
      console.error('Error attaching quotation:', error);
      toast.error('Error attaching quotation: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form when modal is closed or opened
  useEffect(() => {

    
    if (!isOpen) {
      // Reset state when modal closes
      setItems([]);
      setVats({});
      setVatTotal(0);
      setGrandTotal(0);
      setComments({});
      setVisibleComments({});
      setSelectedSupplierCode('');
      setAvailableSuppliers([]);
      setFormData({
        company_name: '',
        tel: '',
        physical_address: '',
        email_address: '',
        quotation_files: []
      });
      setAttachments([]);
    }
  }, [isOpen]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={existingQuote ? "Edit Quotation" : "Attach Quotation"}
      size="2xl"
    >
      <form 
        onSubmit={handleSubmit} 
        className="max-h-[75vh] overflow-y-auto px-1"
        onClick={(e) => {
          // Don't propagate clicks from inputs and buttons
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') {
            e.stopPropagation();
          }
        }}
      >
        {/* Top row with file upload and supplier details side by side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          {/* File upload section with professional styling */}
          <div className="md:col-span-2">
            <div className="bg-white border border-gray-100 rounded-md p-3">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0 w-8 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h4 className="text-sm font-medium text-gray-700 ml-2">Upload Quotation File(s)</h4>
              </div>
              <MultiFileInput
                files={formData.quotation_files}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                maxSize={20 * 1024 * 1024} // 20MB
                multiple={true}
                className="w-full"
              />
              
              {/* Display both selected files and existing attachments */}
              {attachments.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">
                    {attachments.some(a => a instanceof File) ? 'Selected Files' : 'Current Files'}
                    {attachments.some(a => a instanceof File) && attachments.some(a => a.attachment_id) && ' & Existing Files'}
                  </h5>
                  <div className="space-y-2">
                    {attachments.map((attachment, index) => {
                      // Check if this is a File object (newly selected) or attachment object (existing)
                      const isFile = attachment instanceof File;
                      const fileName = isFile ? attachment.name : attachment.file_name;
                      const fileSize = isFile ? attachment.size : attachment.file_size;
                      
                      return (
                        <div key={isFile ? `file-${index}` : `att-${attachment.attachment_id}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center">
                            <svg className="h-4 w-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{fileName}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({Math.round(fileSize / 1024)} KB)
                            </span>
                            {isFile && (
                              <span className="ml-2 text-xs text-blue-600 font-medium">(will be uploaded)</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {!isFile && (
                              <button
                                type="button"
                                onClick={() => handleFileDownload(attachment.file_path)}
                                className="p-1 text-blue-600 hover:text-blue-800"
                                title="Download"
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                if (isFile) {
                                  // Remove from selected files
                                  setAttachments(prev => prev.filter((_, i) => i !== index));
                                  toast.success('File removed from selection');
                                } else {
                                  // Delete existing attachment
                                  handleFileDelete(attachment.file_path);
                                }
                              }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title={isFile ? "Remove" : "Delete"}
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Supplier details with icons and values only - no labels */}
          <div className="md:col-span-1">
            <div className="bg-white border border-gray-100 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Supplier Details</h4>
              
              {/* Show message when no suppliers are available */}
              {availableSuppliers.length === 0 && specification?.specification_supplier?.length > 0 && (
                <div className="text-sm text-gray-500 text-center py-4 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="font-medium">All suppliers have submitted quotes</div>
                  <div className="text-xs mt-1">No additional quotations can be attached</div>
                </div>
              )}
              
              <div className="space-y-3">
                {/* Supplier Selection - show dropdown only if multiple suppliers available and no supplier pre-selected */}
                {availableSuppliers.length > 1 && !selectedSupplier && (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <select
                        value={selectedSupplierCode}
                        onChange={handleSupplierChange}
                        className="block w-full h-8 px-0 border-0 border-b border-gray-200 focus:ring-0 focus:border-blue-500 text-sm bg-transparent"
                        required
                      >
                        <option value="">Select Supplier</option>
                        {availableSuppliers.map(supplier => (
                          <option key={supplier.code} value={supplier.code}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Show selected supplier as read-only when only one supplier, when selected from dropdown, or when pre-selected */}
                {(availableSuppliers.length === 1 || selectedSupplier) && (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <div className="text-sm text-gray-700 py-1 border-b border-gray-200">
                        {selectedSupplier ? selectedSupplier.company_name : availableSuppliers[0]?.name}
                        <span className="text-xs text-gray-500 ml-2">
                          {selectedSupplier ? '(Pre-selected)' : '(Auto-selected)'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Company Name - always displayed */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2-1a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1H6z" clipRule="evenodd" />
                      <path d="M8 7a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    </svg>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="block w-full h-8 px-0 border-0 border-b border-gray-200 focus:ring-0 focus:border-blue-500 text-sm"
                      placeholder="Company Name"
                      required
                      readOnly={availableSuppliers.length > 0 || selectedSupplierCode}
                    />
                  </div>
                </div>

                {/* Email Address - show if has value or suppliers available */}
                {(formData.email_address || specification?.details?.BP_Email || availableSuppliers.length > 0) && (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <input
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        className="block w-full h-8 px-0 border-0 border-b border-gray-200 focus:ring-0 focus:border-blue-500 text-sm"
                        placeholder="Email Address"
                      />
                    </div>
                  </div>
                )}

                {/* Phone Number - show if has value or suppliers available */}
                {(formData.tel || specification?.details?.BP_Phone || availableSuppliers.length > 0) && (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <input
                        type="tel"
                        name="tel"
                        value={formData.tel}
                        onChange={handleInputChange}
                        className="block w-full h-8 px-0 border-0 border-b border-gray-200 focus:ring-0 focus:border-blue-500 text-sm"
                        placeholder="Phone Number"
                      />
                    </div>
                  </div>
                )}

                {/* Physical Address - show if has value or suppliers available */}
                {(formData.physical_address || specification?.details?.BP_Address || availableSuppliers.length > 0) && (
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <input
                        type="text"
                        name="physical_address"
                        value={formData.physical_address}
                        onChange={handleInputChange}
                        className="block w-full h-8 px-0 border-0 border-b border-gray-200 focus:ring-0 focus:border-blue-500 text-sm"
                        placeholder="Physical Address"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Line items table - full width */}
        <div className="bg-white overflow-hidden mb-2">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quotation Line Items</h4>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="min-w-full divide-y divide-gray-200" onMouseDown={(e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') {
                  e.stopPropagation();
                }
              }}>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '40%' }}>
                    Item
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>
                    Qty
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '21%' }}>
                    Amount
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '5%' }}>
                    VAT
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '26%' }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} id={`row-${item.id}`}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <div className="font-medium">{item.name}</div>
                      <div className="mt-1" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleComment(item.id);
                          }}
                          className={`text-xs text-blue-600 hover:text-blue-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 py-1 px-2 rounded z-10 ${visibleComments[item.id] || comments[item.id] ? 'hidden' : ''}`}
                          id={`add-comment-${item.id}`}
                          data-item-id={item.id}
                          style={{ position: 'relative' }}
                        >
                          Add a comment
                        </button>
                        <div className={`mt-1 ${visibleComments[item.id] || comments[item.id] ? '' : 'hidden'}`} id={`comment-div-${item.id}`}>
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={comments[item.id] || ''}
                              onChange={(e) => handleCommentChange(item.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              placeholder="Optional Comment"
                              className="block w-full h-8 text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 z-10"
                              id={`comment-input-${item.id}`}
                              style={{ position: 'relative' }}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleComment(item.id);
                              }}
                              className="ml-2 text-xs text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 text-center">
                      {item.quantity}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">R</span>
                        </div>
                        <input
                          type="text"
                          value={item.unitPrice}
                          onChange={(e) => handleItemPriceChange(item.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 h-8 text-sm border-gray-300 rounded-md z-10"
                          style={{ minWidth: "120px", position: 'relative' }}
                          placeholder="0.00"
                          id={`amount-input-${item.id}`}
                          data-item-id={item.id}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 text-center">
                      <input
                        type="checkbox"
                        checked={item.includeVat}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleVat(item.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer z-10"
                        id={`vat-checkbox-${item.id}`}
                        data-item-id={item.id}
                        style={{ position: 'relative' }}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">R</span>
                        </div>
                        <input
                          type="text"
                          value={formatCurrency(item.total || 0)}
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 h-8 text-sm border-gray-300 rounded-md bg-gray-50"
                          style={{ minWidth: "120px" }}
                          readOnly
                        />
                      </div>
                      {item.includeVat && vats[item.id] > 0 && (
                        <div className="text-xs text-gray-500">
                          VAT: R {formatCurrency(vats[item.id] || 0)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                
                {/* VAT Total Row */}
                <tr className="bg-gray-50">
                  <td colSpan="3" className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                    VAT
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">R</span>
                      </div>
                      <input
                        type="text"
                        value={formatCurrency(vatTotal || 0)}
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 h-8 text-sm border-gray-300 rounded-md bg-gray-50"
                        style={{ minWidth: "120px" }}
                        readOnly
                      />
                    </div>
                  </td>
                </tr>
                
                {/* Grand Total Row */}
                <tr className="bg-gray-50 border-t border-gray-300">
                  <td colSpan="3" className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                    Total
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-700 sm:text-sm font-medium">R</span>
                      </div>
                      <input
                        type="text"
                        value={formatCurrency(grandTotal || 0)}
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 h-8 text-sm border-gray-300 rounded-md bg-blue-50 font-medium text-blue-800"
                        style={{ minWidth: "120px" }}
                        readOnly
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Debug section - remove this after fixing */}
        {/* <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <h5 className="text-sm font-medium text-yellow-800 mb-2">Debug Info:</h5>
          <div className="text-xs text-yellow-700 space-y-1">
            <div>VAT Total: {vatTotal}</div>
            <div>Grand Total: {grandTotal}</div>
            <div>Items VAT States: {JSON.stringify(items.map(item => ({ id: item.id, includeVat: item.includeVat, total: item.total })))}</div>
            <div>VATs Object: {JSON.stringify(vats)}</div>
            <div>Calculated VAT: {Object.values(vats).reduce((sum, vat) => sum + (parseFloat(vat) || 0), 0)}</div>
          </div>
        </div> */}
        
        <div className="mt-5 pt-4 flex justify-end space-x-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || availableSuppliers.length === 0}
            className={`inline-flex justify-center px-4 py-2 text-sm font-medium border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              loading || availableSuppliers.length === 0
                ? 'text-gray-400 bg-gray-300 cursor-not-allowed'
                : 'text-white bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : availableSuppliers.length === 0 ? (
              'No Suppliers Available'
            ) : (
              'Submit Quotation'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AttachQuotationModal;
